import { useNavigate } from '@tanstack/react-router';
import { MainLayout } from '../layouts/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, Tent } from 'lucide-react';

function GoogleLogo() {
  return (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.4c-.2 1.3-1.5 3.9-5.4 3.9-3.2 0-5.8-2.7-5.8-6s2.6-6 5.8-6c1.8 0 3.1.8 3.8 1.4l2.6-2.5C16.8 3.4 14.6 2.5 12 2.5 6.9 2.5 2.8 6.7 2.8 12s4.1 9.5 9.2 9.5c5.3 0 8.8-3.7 8.8-8.9 0-.6-.1-1-.2-1.4H12z"
      />
      <path
        fill="#34A853"
        d="M3.9 7.4l3.2 2.4c.9-2 2.7-3.4 4.9-3.4 1.8 0 3.1.8 3.8 1.4l2.6-2.5C16.8 3.4 14.6 2.5 12 2.5c-3.6 0-6.8 2.1-8.1 5.1z"
      />
      <path
        fill="#4A90E2"
        d="M12 21.5c2.5 0 4.7-.8 6.2-2.2l-2.9-2.3c-.8.6-1.9 1-3.3 1-3.2 0-5.8-2.7-5.8-6 0-.8.2-1.6.5-2.3L3.5 7.3C3 8.6 2.8 10.3 2.8 12c0 5.3 4.1 9.5 9.2 9.5z"
      />
      <path
        fill="#FBBC05"
        d="M21 12.6c0-.6-.1-1-.2-1.4H12v3.9h5.4c-.3 1.3-1.1 2.3-2.1 2.9l2.9 2.3c1.7-1.6 2.8-4 2.8-7.7z"
      />
    </svg>
  );
}

export function AuthPage() {
  const navigate = useNavigate();
  const { loginWithGoogle, isLoading, error, clearError, isAuthenticated } = useAuth();

  if (isAuthenticated) {
    navigate({ to: '/' });
    return null;
  }

  const handleGoogleSignIn = async () => {
    clearError();
    try {
      await loginWithGoogle();
    } catch {
      // Error is handled by auth context.
    }
  };

  return (
    <MainLayout>
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-2xl mb-4">
            <Tent className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome Back</h1>
          <p className="text-gray-600 mt-2">
            Sign in with Google to manage your camping alerts
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full py-3 px-4 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors font-medium text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Redirecting...
              </>
            ) : (
              <>
                <GoogleLogo />
                Continue with Google
              </>
            )}
          </button>

          <p className="mt-4 text-xs text-gray-500 text-center">
            Email/password sign-in is not supported.
          </p>
        </div>
      </div>
    </MainLayout>
  );
}
